import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import {
  DndContext,
  DragOverlay,
  MeasuringStrategy,
  MouseSensor,
  TouchSensor,
  closestCenter,
  defaultDropAnimationSideEffects,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type Active,
  type CollisionDetection,
  type DroppableContainer,
  type DragEndEvent,
  type DragStartEvent,
  type DropAnimation,
  type MeasuringConfiguration,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import {
  Box,
  DND_DRAGGABLE_PROPS,
  DND_OVERLAY_PROPS,
  DND_TOUCH_ACTIVATION,
  Icon,
  Text,
} from "@salestrak/ui";

import type { FormPaletteEntry } from "#app/components/forms/form-builder/form-item-palette";
import type { FormCondition, FormItem } from "#app/lib/forms";

/** One DndContext, three drags that never mix: a card reorders, a palette row mints an item at a seam, a follow-up row reorders within its question. */

const PALETTE_DRAG_KEY = "formPaletteEntry";
const SEAM_INDEX_KEY = "formInsertIndex";
const SEAM_CONDITION_KEY = "formInsertCondition";
const CARD_OUTLINE_KEY = "formCardOutline";
const FOLLOW_UP_KEY = "formFollowUp";

type FollowUpDrag = {
  itemId: string;
  controllerId: string;
};

function followUpOf(container: {
  data: { current?: Record<string, unknown> | undefined };
}): FollowUpDrag | undefined {
  return container.data.current?.[FOLLOW_UP_KEY] as FollowUpDrag | undefined;
}

export type CardOutline = {
  index: number;
  depth: number;
  parentId: string | null;
  controllerIndex: number | null;
};

function outlineOf(container: {
  data: { current?: Record<string, unknown> | undefined };
}): CardOutline | undefined {
  return container.data.current?.[CARD_OUTLINE_KEY] as CardOutline | undefined;
}
/** A palette drag must be inside this rect for a seam to claim it, so a drop over the palette lands nowhere. */
const CANVAS_REGION_KEY = "formCanvasRegion";

type FormDragState = "none" | "palette" | "card" | "follow-up";

const FormDragContext = createContext<FormDragState>("none");

export function useFormDragState(): FormDragState {
  return useContext(FormDragContext);
}

function paletteEntryOf(
  active: { data: { current?: Record<string, unknown> | undefined } } | null,
): FormPaletteEntry | null {
  const entry = active?.data.current?.[PALETTE_DRAG_KEY] as
    | FormPaletteEntry
    | undefined;
  return entry ?? null;
}

const SETTLE_EASING = "cubic-bezier(0.2, 0, 0, 1)";

export const SORT_TRANSITION = { duration: 200, easing: SETTLE_EASING };

const CARD_DROP: DropAnimation = {
  duration: 300,
  easing: SETTLE_EASING,
  keyframes: ({ active, transform }) =>
    paletteEntryOf(active)
      ? [{ opacity: "0" }]
      : [
          { transform: CSS.Transform.toString(transform.initial) ?? "" },
          { transform: CSS.Transform.toString(transform.final) ?? "" },
        ],
  sideEffects: (parameters) =>
    paletteEntryOf(parameters.active)
      ? undefined
      : defaultDropAnimationSideEffects({ styles: { active: { opacity: "0" } } })(parameters),
};

/** Rects are re-read on a short clock during a drag: folding follow-ups keep neighbours moving after the lift. */
const MEASURING: MeasuringConfiguration = {
  droppable: { frequency: 60, strategy: MeasuringStrategy.WhileDragging },
};

function isSeam(container: DroppableContainer): boolean {
  return container.data.current?.[SEAM_INDEX_KEY] !== undefined;
}

function isRegion(container: DroppableContainer): boolean {
  return container.data.current?.[CANVAS_REGION_KEY] === true;
}

/** A card drag sees only other cards, a follow-up row only its siblings, a palette drag only seams while inside the canvas. An empty form has no seams, so the canvas is the target (index 0). */
const collisionDetection: CollisionDetection = (args) => {
  const containers = args.droppableContainers;
  const row = followUpOf(args.active);
  if (row) {
    return closestCenter({
      ...args,
      droppableContainers: containers.filter(
        (container) => followUpOf(container)?.controllerId === row.controllerId,
      ),
    });
  }
  if (!paletteEntryOf(args.active)) {
    return closestCenter({
      ...args,
      droppableContainers: cardTargets(args.active, containers),
    });
  }
  const inside = pointerWithin({
    ...args,
    droppableContainers: containers.filter(isRegion),
  });
  if (inside.length === 0) return [];
  const seams = containers.filter(isSeam);
  if (seams.length === 0) return inside;
  return closestCenter({ ...args, droppableContainers: seams });
};

/** Cards move among their siblings and never above their controller; dragging down past a run targets its last card so the block lands after it. */
function cardTargets(
  active: Active | null,
  containers: DroppableContainer[],
): DroppableContainer[] {
  const cards = containers
    .filter((container) => !isSeam(container) && !isRegion(container) && !followUpOf(container))
    .sort((a, b) => (outlineOf(a)?.index ?? 0) - (outlineOf(b)?.index ?? 0));
  const lifted = active ? outlineOf(active) : undefined;
  if (!lifted) return cards;
  const targets: DroppableContainer[] = [];
  for (let at = 0; at < cards.length; at += 1) {
    const card = cards[at]!;
    const outline = outlineOf(card);
    if (!outline || outline.parentId !== lifted.parentId) continue;
    if (outline.index === lifted.index) continue;
    if (lifted.controllerIndex !== null && outline.index <= lifted.controllerIndex) continue;
    if (outline.index < lifted.index) {
      targets.push(card);
      continue;
    }
    let tail = card;
    for (let below = at + 1; below < cards.length; below += 1) {
      const deeper = outlineOf(cards[below]!);
      if (!deeper || deeper.depth <= outline.depth) break;
      tail = cards[below]!;
    }
    targets.push(tail);
  }
  return targets;
}

function PaletteDragGhost({ entry }: { entry: FormPaletteEntry }) {
  return (
    <Box
      {...DND_OVERLAY_PROPS}
      align="center"
      background="elevated"
      border
      cursor="grabbing"
      direction="row"
      fill="width"
      gap="sm"
      minHeight={32}
      paddingX="sm"
      rounded="sm"
      shadow="lg"
    >
      <Box align="center" flex="none" justify="center" width={16}>
        <Icon color={entry.iconColor ?? "muted"} name={entry.icon} size="md" />
      </Box>
      <Text size="sm" truncate={1}>
        {entry.label}
      </Text>
    </Box>
  );
}

export function FormBuilderDndScope({
  onInsert,
  onReorder,
  renderCard,
  renderFollowUp,
  children,
}: {
  onInsert: (index: number, item: FormItem) => void;
  onReorder: (activeId: string, overId: string) => void;
  renderCard: (itemId: string) => ReactNode;
  renderFollowUp: (itemId: string) => ReactNode;
  children: ReactNode;
}) {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: DND_TOUCH_ACTIVATION }),
  );
  const [lifted, setLifted] = useState<FormPaletteEntry | null>(null);
  const [liftedCard, setLiftedCard] = useState<string | null>(null);
  const [liftedFollowUp, setLiftedFollowUp] = useState<string | null>(null);
  const [state, setState] = useState<FormDragState>("none");

  const onDragStart = useCallback(({ active }: DragStartEvent) => {
    const entry = paletteEntryOf(active);
    const row = followUpOf(active);
    setLifted(entry);
    setLiftedFollowUp(row?.itemId ?? null);
    setLiftedCard(entry || row ? null : String(active.id));
    setState(entry ? "palette" : row ? "follow-up" : "card");
  }, []);
  const onDragCancel = useCallback(() => {
    setLifted(null);
    setLiftedCard(null);
    setLiftedFollowUp(null);
    setState("none");
  }, []);
  const onDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      const entry = paletteEntryOf(active);
      const row = followUpOf(active);
      setLifted(null);
      setLiftedCard(null);
      setLiftedFollowUp(null);
      setState("none");
      if (row) {
        const target = over ? followUpOf(over) : undefined;
        if (target && target.itemId !== row.itemId) onReorder(row.itemId, target.itemId);
        return;
      }
      if (entry) {
        if (!over) return;
        const index = over.data.current?.[SEAM_INDEX_KEY] as number | undefined;
        const showWhen = over.data.current?.[SEAM_CONDITION_KEY] as FormCondition | undefined;
        const minted = entry.mint();
        onInsert(
          index ?? 0,
          showWhen && minted.kind !== "section" ? { ...minted, showWhen } : minted,
        );
        return;
      }
      if (over && active.id !== over.id) onReorder(String(active.id), String(over.id));
    },
    [onInsert, onReorder],
  );

  return (
    <DndContext
      collisionDetection={collisionDetection}
      measuring={MEASURING}
      onDragCancel={onDragCancel}
      onDragEnd={onDragEnd}
      onDragStart={onDragStart}
      sensors={sensors}
    >
      <FormDragContext.Provider value={state}>{children}</FormDragContext.Provider>
      {/* The overlay is fixed to the viewport so the held card is never clipped by a panel or scroll box. */}
      <DragOverlay dropAnimation={CARD_DROP}>
        {lifted ? (
          <PaletteDragGhost entry={lifted} />
        ) : liftedFollowUp ? (
          renderFollowUp(liftedFollowUp)
        ) : liftedCard ? (
          renderCard(liftedCard)
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

export function usePaletteDrag(
  entry: FormPaletteEntry,
  scope?: string,
  inert = false,
): {
  ref: (node: unknown) => void;
  isDragging: boolean;
  dragProps: Record<string, unknown>;
  listeners: Record<string, unknown>;
} {
  const disabled = inert || Boolean(entry.disabledReason);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    data: { [PALETTE_DRAG_KEY]: entry },
    disabled,
    id: scope ? `form-palette:${scope}:${entry.key}` : `form-palette:${entry.key}`,
  });
  const ref = useCallback(
    (node: unknown) => setNodeRef(node as HTMLElement | null),
    [setNodeRef],
  );
  const dragProps = useMemo(
    () =>
      disabled
        ? {}
        : {
            ...DND_DRAGGABLE_PROPS,
            ...(attributes as unknown as Record<string, unknown>),
            ...(listeners as Record<string, unknown>),
          },
    [attributes, disabled, listeners],
  );
  const dragListeners = useMemo(
    () =>
      disabled ? {} : { ...DND_DRAGGABLE_PROPS, ...(listeners as Record<string, unknown>) },
    [disabled, listeners],
  );
  return { dragProps, isDragging, listeners: dragListeners, ref };
}

export function useInsertSeamDrop(
  index: number,
  inherit: FormCondition | undefined,
): {
  ref: (node: unknown) => void;
  isOver: boolean;
} {
  const { isOver, setNodeRef } = useDroppable({
    data: { [SEAM_INDEX_KEY]: index, [SEAM_CONDITION_KEY]: inherit },
    id: `form-seam:${index}`,
  });
  const ref = useCallback(
    (node: unknown) => setNodeRef(node as HTMLElement | null),
    [setNodeRef],
  );
  return { isOver, ref };
}

export function useCanvasDropRegion(): (node: unknown) => void {
  const { setNodeRef } = useDroppable({
    data: { [CANVAS_REGION_KEY]: true },
    id: "form-canvas-region",
  });
  return useCallback(
    (node: unknown) => setNodeRef(node as HTMLElement | null),
    [setNodeRef],
  );
}

export function cardOutlineData(outline: CardOutline): Record<string, CardOutline> {
  return { [CARD_OUTLINE_KEY]: outline };
}

export function followUpRowId(itemId: string): string {
  return `form-follow-up:${itemId}`;
}

export function followUpDragData(drag: FollowUpDrag): Record<string, FollowUpDrag> {
  return { [FOLLOW_UP_KEY]: drag };
}
